// Human labels for the Reports period picker and its date ranges ("Oct 1–20 vs Sep 1–20"). English, built from
// fixed month names on `YYYY-MM-DD` parts — no Intl, so no ICU differences between Node, browsers and CI.
// ---------------------------------------------------------------------------
import type { ReportDateWindow, ReportPeriod, ReportPeriodDates } from './report-periods';

export const REPORT_PERIOD_OPTIONS: ReadonlyArray<{ value: ReportPeriod; label: string }> = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-7-days', label: 'Last 7 days' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'last-90-days', label: 'Last 90 days' },
  { value: 'this-year', label: 'This year' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The picker label of a period ("Last 30 days"). */
export function reportPeriodLabel(period: ReportPeriod): string {
  return REPORT_PERIOD_OPTIONS.find((option) => option.value === period)!.label;
}

/**
 * "Oct 20" (one day) · "Oct 1–20" (one month) · "Sep 28 – Oct 4" (two months) · "Dec 28, 2025 – Jan 3, 2026"
 * (across a new year, always with years). `withYear` adds the year to a same-year range: "Jan 1 – Oct 20, 2026".
 */
export function formatReportPeriodRange(window: ReportDateWindow, options: { withYear?: boolean } = {}): string {
  const [fromYear, fromMonth, fromDay] = window.fromIso.split('-').map(Number) as [number, number, number];
  const [toYear, toMonth, toDay] = window.toIso.split('-').map(Number) as [number, number, number];
  const from = `${MONTHS[fromMonth - 1]} ${fromDay}`;
  const to = `${MONTHS[toMonth - 1]} ${toDay}`;
  if (fromYear !== toYear) return `${from}, ${fromYear} – ${to}, ${toYear}`;
  const year = options.withYear ? `, ${toYear}` : '';
  if (window.fromIso === window.toIso) return `${from}${year}`;
  if (fromMonth === toMonth) return `${from}–${toDay}${year}`;
  return `${from} – ${to}${year}`;
}

/**
 * Both windows of a resolved period, labelled. "This year" names the years ("Jan 1 – Oct 20, 2026" vs
 * "… 2025"), since the same dates would otherwise read identically.
 */
export function formatReportPeriodComparison(
  period: ReportPeriod,
  dates: Pick<ReportPeriodDates, 'current' | 'previous'>,
): { current: string; previous: string } {
  const withYear = period === 'this-year';
  return {
    current: formatReportPeriodRange(dates.current, { withYear }),
    previous: formatReportPeriodRange(dates.previous, { withYear }),
  };
}
